/**
 * marketplaceSetup store — ONE cached session read of
 * `GET /api/marketplace/pro/setup` (plan 443), replacing the six independent
 * callers (the no-offers banner, the landing gate, Browse, the Composer,
 * Standing offers and Profile).
 *
 * Follows the module-level idiom of `stores/proHome.ts`: a zustand store plus
 * a module-level in-flight promise, so any number of mounted consumers share
 * ONE request.
 *
 * Freshness, with no interval polling:
 *   - a consumer mounting over cached data renders it at once; the data is
 *     refetched in the background only when older than `STALE_MS`.
 *   - the `marketplace_setup` dirty event refetches at once while a consumer
 *     is mounted, otherwise marks the cache stale for the next mount. Events
 *     (and `refreshMarketplaceSetup`) landing during a fetch queue exactly one
 *     follow-up fetch.
 *   - `refreshMarketplaceSetup()` is the explicit refetch for the surfaces
 *     that mutate setup (availability, standing offers, blurb, service area,
 *     verification): it always issues a request that STARTS after the call, so
 *     a response that predates the save can never be what the caller awaits.
 *
 * A failed fetch keeps the last good data and reports `error`; consumers
 * decide whether that matters (the banner fails quiet, the gates fail open /
 * closed per their own contracts).
 *
 * IS_PREVIEW: the store never fetches. Each preview surface keeps its own
 * fixture (they intentionally differ per page).
 */
import { useEffect } from "react";
import { create } from "zustand";

import { useProToken, type GetProToken } from "../hooks/useProToken";
import {
  getMarketplaceSetup,
  type MarketplaceDescriptors,
  type MarketplaceSetupView,
} from "../lib/api";
import { IS_PREVIEW } from "../lib/pro-mode";
import {
  PRO_RESOURCES_DIRTY_EVENT,
  type ProResourcesDirtyDetail,
} from "../lib/pro-resource-dirty";

/** Cached data older than this is refetched (in the background) on mount. */
export const SETUP_STALE_MS = 60_000;

interface MarketplaceSetupState {
  /** Null until a fetch has succeeded. */
  data: MarketplaceSetupView | null;
  /** The last fetch's error; cleared by the next success. */
  error: unknown;
  /** First load in flight, nothing to show yet. */
  isLoading: boolean;
  hasLoaded: boolean;
  /** `Date.now()` of the last successful fetch. */
  fetchedAt: number | null;
  /** Invalidated while nobody was mounted: the next attach refetches. */
  stale: boolean;
  /** Register a mounted consumer; returns its detach. Loads what is missing. */
  attach: (consumer: Consumer) => () => void;
  /** Dirty-event path: refetch now if a consumer is mounted, else mark stale. */
  invalidate: () => void;
  /** Apply a saved descriptor set to the cached view (no-op until loaded). */
  setDescriptors: (descriptors: MarketplaceDescriptors) => void;
  reset: () => void;
}

interface Consumer {
  getToken: GetProToken;
}

const consumers = new Set<Consumer>();
let inFlight: Promise<MarketplaceSetupView> | null = null;
let followUp: Promise<MarketplaceSetupView> | null = null;
// Bumped by reset() so a fetch that outlives it cannot write into fresh state.
let generation = 0;

function initialState() {
  return {
    data: null,
    error: null,
    isLoading: false,
    hasLoaded: false,
    fetchedAt: null,
    stale: false,
  };
}

function anyToken(): GetProToken {
  const [first] = consumers;
  return first ? first.getToken : () => Promise.resolve(null);
}

/** Start a request now (joins one already running). */
function request(): Promise<MarketplaceSetupView> {
  if (inFlight) return inFlight;
  const gen = generation;
  const hadData = useMarketplaceSetupStore.getState().hasLoaded;
  if (!hadData) useMarketplaceSetupStore.setState({ isLoading: true, error: null });

  const run = (async () => {
    try {
      const token = (await anyToken()()) ?? undefined;
      const data = await getMarketplaceSetup(token);
      if (gen === generation) {
        useMarketplaceSetupStore.setState({
          data,
          error: null,
          isLoading: false,
          hasLoaded: true,
          fetchedAt: Date.now(),
          stale: false,
        });
      }
      return data;
    } catch (err) {
      if (gen === generation) {
        useMarketplaceSetupStore.setState({ error: err, isLoading: false, stale: true });
      }
      throw err;
    }
  })();
  const tracked = run.finally(() => {
    if (inFlight === tracked) inFlight = null;
  });
  inFlight = tracked;
  return tracked;
}

/**
 * A request that STARTS after this call: joins nothing already running, but
 * coalesces with a follow-up that is already queued behind it.
 */
function requestFresh(): Promise<MarketplaceSetupView> {
  if (!inFlight) return request();
  if (!followUp) {
    const queued: Promise<MarketplaceSetupView> = inFlight
      .catch(() => undefined)
      .then(() => {
        followUp = null;
        return request();
      });
    followUp = queued;
  }
  return followUp;
}

export const useMarketplaceSetupStore = create<MarketplaceSetupState>((set, get) => ({
  ...initialState(),

  attach: (consumer) => {
    consumers.add(consumer);
    const { hasLoaded, stale, fetchedAt } = get();
    const old = fetchedAt === null || Date.now() - fetchedAt > SETUP_STALE_MS;
    if (!hasLoaded || stale || old) request().catch(() => {});
    return () => {
      consumers.delete(consumer);
    };
  },

  invalidate: () => {
    if (consumers.size > 0) requestFresh().catch(() => {});
    else set({ stale: true });
  },

  setDescriptors: (descriptors) =>
    set((state) => (state.data ? { data: { ...state.data, descriptors } } : {})),

  reset: () => {
    generation += 1;
    consumers.clear();
    inFlight = null;
    followUp = null;
    set(initialState());
  },
}));

/**
 * Refetch after a mutation. Resolves with the fresh view and REJECTS on
 * failure (the caller shows its own error); every mounted consumer updates.
 */
export function refreshMarketplaceSetup(): Promise<MarketplaceSetupView> {
  return requestFresh();
}

// A setup change (availability, standing offers, service area, card) emitted
// anywhere in the app.
if (typeof window !== "undefined") {
  window.addEventListener(PRO_RESOURCES_DIRTY_EVENT, (event) => {
    const detail = (event as CustomEvent<ProResourcesDirtyDetail>).detail;
    if (detail?.resources?.includes("marketplace_setup")) {
      useMarketplaceSetupStore.getState().invalidate();
    }
  });
}

export interface MarketplaceSetupRead {
  data: MarketplaceSetupView | null;
  error: unknown;
  isLoading: boolean;
}

/**
 * Setup consumer. `enabled: false` (e.g. the marketplace flag is off) issues
 * no request. Under IS_PREVIEW the hook never fetches; callers seed their own
 * fixture.
 */
export function useMarketplaceSetup(options?: { enabled?: boolean }): MarketplaceSetupRead {
  const enabled = options?.enabled ?? true;
  const getToken = useProToken();
  useEffect(() => {
    if (IS_PREVIEW || !enabled) return;
    return useMarketplaceSetupStore.getState().attach({ getToken });
  }, [getToken, enabled]);
  const data = useMarketplaceSetupStore((s) => s.data);
  const error = useMarketplaceSetupStore((s) => s.error);
  const isLoading = useMarketplaceSetupStore((s) => s.isLoading);
  return { data, error, isLoading };
}
